import {
  COUNTRY_ISO_TO_LABEL,
  DEFAULT_COUNTRY,
  isAllowedCountry,
} from '@/lib/constants'

export interface AddressComponentLike {
  types: string[]
  longText?: string
  shortText?: string
  long_name?: string
  short_name?: string
}

export interface ParsedAddress {
  country: string
  city: string
  postalCode: string
  address: string
  latitude?: number
  longitude?: number
}

function componentText(component: AddressComponentLike): string {
  return (
    component.longText ??
    component.long_name ??
    component.shortText ??
    component.short_name ??
    ''
  )
}

function componentShortText(component: AddressComponentLike): string {
  return component.shortText ?? component.short_name ?? componentText(component)
}

function findComponent(
  components: AddressComponentLike[],
  ...types: string[]
): AddressComponentLike | undefined {
  return components.find(c => types.some(type => c.types.includes(type)))
}

function resolveCountry(
  components: AddressComponentLike[],
  fallbackCountry: string
): string {
  const countryComponent = findComponent(components, 'country')
  if (!countryComponent) return fallbackCountry

  const iso = componentShortText(countryComponent).toUpperCase()
  const mapped = COUNTRY_ISO_TO_LABEL[iso]
  if (mapped && isAllowedCountry(mapped)) {
    return mapped
  }

  const longName = componentText(countryComponent)
  if (isAllowedCountry(longName)) {
    return longName
  }

  return fallbackCountry
}

function resolveCity(components: AddressComponentLike[]): string {
  const cityComponent = findComponent(
    components,
    'locality',
    'postal_town',
    'administrative_area_level_2'
  )
  return cityComponent ? componentText(cityComponent) : ''
}

function resolvePostalCode(components: AddressComponentLike[]): string {
  const postalComponent = findComponent(components, 'postal_code')
  return postalComponent ? componentText(postalComponent) : ''
}

function resolveStreetAddress(components: AddressComponentLike[]): string {
  const streetNumber = findComponent(components, 'street_number')
  const route = findComponent(components, 'route')
  const parts = [streetNumber, route]
    .map(c => (c ? componentText(c) : ''))
    .filter(Boolean)

  return parts.join(' ')
}

/** Pure parser used by unit tests and {@link parsePlaceAddress}. */
export function parseAddressComponents(
  components: AddressComponentLike[],
  options: {
    formattedAddress?: string
    latitude?: number
    longitude?: number
    fallbackCountry?: string
  } = {}
): ParsedAddress {
  const fallbackCountry = options.fallbackCountry ?? DEFAULT_COUNTRY
  const street = resolveStreetAddress(components)

  return {
    country: resolveCountry(components, fallbackCountry),
    city: resolveCity(components),
    postalCode: resolvePostalCode(components),
    address: street || options.formattedAddress || '',
    latitude: options.latitude,
    longitude: options.longitude,
  }
}

function readLatLng(
  location: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined
): { latitude?: number; longitude?: number } {
  if (!location) return {}

  if (typeof (location as google.maps.LatLng).lat === 'function') {
    const latLng = location as google.maps.LatLng
    return { latitude: latLng.lat(), longitude: latLng.lng() }
  }

  const literal = location as google.maps.LatLngLiteral
  if (typeof literal.lat === 'number' && typeof literal.lng === 'number') {
    return { latitude: literal.lat, longitude: literal.lng }
  }

  return {}
}

/** Parse a Place returned by the Places Autocomplete Data API after fetchFields. */
export function parsePlaceAddress(
  place: google.maps.places.Place,
  fallbackCountry: string = DEFAULT_COUNTRY
): ParsedAddress {
  const components = (place.addressComponents ?? []) as AddressComponentLike[]
  const coords = readLatLng(place.location)

  return parseAddressComponents(components, {
    formattedAddress: place.formattedAddress ?? undefined,
    latitude: coords.latitude,
    longitude: coords.longitude,
    fallbackCountry,
  })
}
